import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedIcon from '@mui/icons-material/Verified';
import { getPublicProfile, PublicProfile } from '../api/public-profile';
import { PortfolioItem } from '../api/portfolio';
import { PURPLE_AVATAR as PURPLE } from '../theme/tokens';
import StackedCard from '../components/StackedCard';
import { isMultiBadge } from '../constants/badgeTypes';

function formatDate(iso: string | null) {
    if (!iso) return null;
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function BadgeCard({ item, onClick }: { item: PortfolioItem; onClick: () => void }) {
    return (
        <StackedCard enabled={isMultiBadge(item.badgeType)}>
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardActionArea onClick={onClick} sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                <Box sx={{ bgcolor: PURPLE, py: 3, display: 'flex', justifyContent: 'center' }}>
                    <Avatar
                        src={item.badgeImage ?? undefined}
                        variant="rounded"
                        sx={{ width: 84, height: 84, bgcolor: 'rgba(255,255,255,0.15)' }}
                    >
                        {item.badgeName.charAt(0).toUpperCase()}
                    </Avatar>
                </Box>
                <Box sx={{ p: 2, flexGrow: 1 }}>
                    <Typography fontWeight={700} noWrap title={item.badgeName}>{item.badgeName}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap title={item.issuerName}>
                        {item.issuerName}
                    </Typography>
                    {formatDate(item.issuedDate) && (
                        <Typography variant="caption" color="text.secondary">
                            Earned {formatDate(item.issuedDate)}
                        </Typography>
                    )}
                    {item.endorsementCount > 0 && (
                        <Box mt={1}>
                            <Chip
                                size="small"
                                icon={<VerifiedIcon />}
                                label={`${item.endorsementCount} endorsement${item.endorsementCount === 1 ? '' : 's'}`}
                            />
                        </Box>
                    )}
                </Box>
            </CardActionArea>
        </Card>
        </StackedCard>
    );
}

function BadgeDialog({ item, onClose }: { item: PortfolioItem | null; onClose: () => void }) {
    if (!item) return null;
    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pr: 6 }}>
                {item.badgeName}
                <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Box display="flex" gap={2} mb={2}>
                    <Avatar src={item.badgeImage ?? undefined} variant="rounded" sx={{ width: 72, height: 72, bgcolor: PURPLE }}>
                        {item.badgeName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                        <Typography variant="body2" color="text.secondary">{item.issuerName}</Typography>
                        {formatDate(item.issuedDate) && (
                            <Typography variant="caption" color="text.secondary" display="block">
                                Earned {formatDate(item.issuedDate)}
                            </Typography>
                        )}
                        {formatDate(item.expiresOn) && (
                            <Typography variant="caption" color="text.secondary" display="block">
                                Expires {formatDate(item.expiresOn)}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {item.badgeDescription && (
                    <>
                        <Typography variant="subtitle2" gutterBottom>Description</Typography>
                        <Box sx={{ '& p': { m: 0 }, mb: 2 }} dangerouslySetInnerHTML={{ __html: item.badgeDescription }} />
                    </>
                )}
                {item.badgeCriteria && (
                    <>
                        <Typography variant="subtitle2" gutterBottom>Criteria</Typography>
                        <Box sx={{ '& p': { m: 0 }, mb: 2 }} dangerouslySetInnerHTML={{ __html: item.badgeCriteria }} />
                    </>
                )}
                {item.tags.length > 0 && (
                    <Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
                        {item.tags.map(t => <Chip key={t} size="small" label={t} />)}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default function PublicProfilePage() {
    const { slug } = useParams<{ slug: string }>();
    const [profile, setProfile] = useState<PublicProfile | null>(null);
    const [badges, setBadges] = useState<PortfolioItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [selected, setSelected] = useState<PortfolioItem | null>(null);

    useEffect(() => {
        if (!slug) return;
        setLoading(true);
        setNotFound(false);
        getPublicProfile(slug)
            .then(res => {
                if (res.success && res.data) {
                    setProfile(res.data.profile);
                    setBadges(res.data.badges);
                } else {
                    setNotFound(true);
                }
            })
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress />
            </Box>
        );
    }

    if (notFound || !profile) {
        return (
            <Container maxWidth="sm" sx={{ py: 6 }}>
                <Alert severity="info">
                    This profile is private or doesn&apos;t exist.
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            {/* Identity header */}
            <Box textAlign="center" mb={4}>
                <Avatar
                    src={profile.profileImage ?? undefined}
                    sx={{ width: 110, height: 110, mx: 'auto', mb: 2, bgcolor: 'grey.400', fontSize: 44 }}
                >
                    {profile.displayName.charAt(0).toUpperCase()}
                </Avatar>
                <Typography variant="h4" fontWeight={700}>
                    {profile.displayName}&apos;s Public Learning Record
                </Typography>
                {profile.bio && (
                    <Box
                        sx={{ mt: 1, color: 'text.secondary', '& p': { m: 0 } }}
                        dangerouslySetInnerHTML={{ __html: profile.bio }}
                    />
                )}
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Typography variant="h6" fontWeight={700} mb={2}>
                Badges ({badges.length})
            </Typography>

            {badges.length === 0 ? (
                <Typography color="text.secondary">No badges shared yet.</Typography>
            ) : (
                <Grid container spacing={2}>
                    {badges.map(b => (
                        <Grid item xs={12} sm={6} md={4} key={b.issuedBadgeID}>
                            <BadgeCard item={b} onClick={() => setSelected(b)} />
                        </Grid>
                    ))}
                </Grid>
            )}

            <BadgeDialog item={selected} onClose={() => setSelected(null)} />
        </Container>
    );
}
